import React from 'react';
import { motion } from 'framer-motion';
import { Receipt, CreditCard, Store, Landmark, PieChart } from 'lucide-react';
import type { ArgentinaMetrics, CountryCode } from '../utils/calculations';
import { formatCurrency } from '../utils/calculations';
interface CommissionsBreakdownProps {
  metrics: ArgentinaMetrics;
  country: CountryCode;
}
export function CommissionsBreakdown({
  metrics,
  country
}: CommissionsBreakdownProps) {
  const commissions = [{
    label: 'Mercado Pago',
    amount: metrics.mercadoPagoAmount,
    icon: CreditCard,
    color: 'text-blue-400',
    bg: 'bg-blue-400/10',
    border: 'border-blue-400/20'
  }, {
    label: 'Cuotas',
    amount: metrics.cuotasAmount,
    icon: PieChart,
    color: 'text-purple-400',
    bg: 'bg-purple-400/10',
    border: 'border-purple-400/20',
    subtext: `Tasa real: ${metrics.comisionCuotasReal}%`
  }, {
    label: 'IIBB',
    amount: metrics.iibbAmount,
    icon: Landmark,
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10',
    border: 'border-yellow-400/20'
  }, {
    label: 'Tienda Nube / Shopify',
    amount: metrics.tiendaAmount,
    icon: Store,
    color: 'text-cyber-green',
    bg: 'bg-cyber-green/10',
    border: 'border-cyber-green/20'
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: 0.4
  }} className="glass-card p-6 md:p-8 lg:col-span-2" data-id="element-17">
      <div className="flex items-center justify-center gap-3 mb-8" data-id="element-18">
        <Receipt className="w-5 h-5 text-white/70" data-id="element-19" />
        <h2 className="text-sm font-bold tracking-widest text-white uppercase" data-id="element-20">
          Desglose de Comisiones e Impuestos
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-21">
        {commissions.map((item, index) => <motion.div key={item.label} initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.5 + index * 0.1
      }} className={`p-4 rounded-xl border ${item.border} ${item.bg} flex flex-col items-center text-center relative overflow-hidden group`} data-id="element-22">
            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-23"></div>
            <item.icon className={`w-6 h-6 mb-3 ${item.color}`} data-id="element-24" />
            <h3 className="text-xs font-bold text-white/70 uppercase tracking-wider mb-1" data-id="element-25">
              {item.label}
            </h3>
            <div className={`text-xl font-black ${item.color}`} data-id="element-26">
              {formatCurrency(item.amount, country)}
            </div>
            {item.subtext && <div className="text-[10px] text-white/40 mt-1 font-medium" data-id="element-27">
                {item.subtext}
              </div>}
          </motion.div>)}
      </div>

      <div className="mt-6 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-28">
        <div className="text-center md:text-left" data-id="element-29">
          <div className="text-xs font-bold text-white/50 uppercase tracking-wider mb-1" data-id="element-30">
            Total Comisiones
          </div>
          <div className="text-2xl font-black text-white" data-id="element-31">
            {formatCurrency(metrics.totalComisiones, country)}
          </div>
        </div>
        <div className="hidden md:block w-px h-12 bg-white/10" data-id="element-32"></div>
        <div className="text-center md:text-right" data-id="element-33">
          <div className="text-xs font-bold text-white/50 uppercase tracking-wider mb-1" data-id="element-34">
            Total Costos + Comisiones
          </div>
          <div className="text-2xl font-black text-salmon" data-id="element-35">
            {formatCurrency(metrics.totalCostosComisiones, country)}
          </div>
        </div>
      </div>
    </motion.div>;
}